import React from 'react';
import PropTypes from 'prop-types';
import { Phone, Mail, FileText, ArrowUpRight, Check } from 'lucide-react';
import Card from '../../../../ui/canon/Card';
import { SingleColumnTemplate } from '../../../../ui/layouts/SingleColumnTemplate';

/**
 * ContactPage
 *
 * Modernized /media/contact. Replaces three Bootstrap ERB partials
 * (_raymond_james_contact, _manulife_contact, _demo_contact) with one layout
 * driven by structured content from MediaContactContent.
 *
 * Design notes:
 * - The contact details are the job of this page, so they lead and are the only
 *   card. Cards elsewhere would be chrome around plain prose — the reference
 *   lists read better as headed sections on the page ground.
 * - No entrance animation. This is a utility page; motion here would be
 *   ornament. The effort goes into the hover and focus states of the three
 *   things a user actually clicks: phone, email, download.
 */

// `border-0 pb-0` is not decoration, it is a reset: application.css styles bare
// h2 with `border-bottom: 1px solid; padding-bottom: 15px`, and that stylesheet
// loads under the succession layout too. Without this the section headings pick
// up a divider and 15px of padding nobody chose.
const SectionHeading = ({ children }) => (
  <h2 className="border-0 pb-0 text-sm font-semibold uppercase tracking-wide text-succession-text-secondary">
    {children}
  </h2>
);

SectionHeading.propTypes = { children: PropTypes.node };
SectionHeading.defaultProps = { children: null };

// A tel: link needs the punctuation stripped; the label keeps it for reading.
const telHref = (phone) => `tel:${(phone || '').replace(/[^\d+]/g, '')}`;

const ContactMethod = ({ icon: Icon, label, value, href }) => (
  <a
    href={href}
    className="group flex items-center gap-3 rounded-md -mx-2 px-2 py-2 transition-colors hover:bg-succession-hover-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring">
    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-succession-bg-secondary text-succession-primary">
      <Icon className="h-4 w-4" aria-hidden />
    </span>
    {/* Stacked with flex rather than Tailwind's `block`: legacy application.css
        defines `.block { padding: 25px 0 }`, and this page renders under the
        findbob_front_end layout where that stylesheet is loaded. Using the
        utility here would silently add 50px of padding to every line. */}
    <span className="flex min-w-0 flex-col">
      <span className="text-xs text-succession-text-secondary">{label}</span>
      <span className="truncate text-sm font-medium text-succession-text-primary group-hover:text-succession-primary transition-colors">
        {value}
      </span>
    </span>
  </a>
);

ContactMethod.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
};

const ContactCard = ({ contact }) => {
  const hasPhone = Boolean(contact && contact.phone);
  const hasEmail = Boolean(contact && contact.email);
  if (!hasPhone && !hasEmail) return null;

  return (
    <Card size="lg" className="w-full">
      <p className="text-sm font-semibold text-succession-text-primary">{contact.team_name}</p>
      <p className="mt-1 text-sm text-succession-text-secondary">
        Reach the team directly — no ticket, no queue.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-4">
        {hasPhone && (
          <ContactMethod icon={Phone} label="Phone" value={contact.phone} href={telHref(contact.phone)} />
        )}
        {hasEmail && (
          <ContactMethod icon={Mail} label="Email" value={contact.email} href={`mailto:${contact.email}`} />
        )}
      </div>
    </Card>
  );
};

ContactCard.propTypes = {
  contact: PropTypes.shape({
    team_name: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
};
ContactCard.defaultProps = { contact: null };

const ContentSection = ({ heading, items }) => (
  <section>
    <SectionHeading>{heading}</SectionHeading>
    <ul className="mt-3 space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-succession-text-primary">
          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-succession-primary" aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </section>
);

ContentSection.propTypes = {
  heading: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const Downloads = ({ downloads }) => (
  <section>
    <SectionHeading>Downloads</SectionHeading>
    <ul className="mt-3 space-y-2">
      {downloads.map((doc) => (
        <li key={doc.url}>
          <a
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-3 rounded-md -mx-2 px-2 py-2 transition-colors hover:bg-succession-hover-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring">
            <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-succession-primary" aria-hidden />
            <span className="flex min-w-0 flex-col">
              <span className="flex items-center gap-1 text-sm font-medium text-succession-text-primary group-hover:text-succession-primary transition-colors">
                {doc.title}
                <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
              </span>
              {doc.description && (
                <span className="mt-0.5 text-sm text-succession-text-secondary leading-relaxed">
                  {doc.description}
                </span>
              )}
            </span>
          </a>
        </li>
      ))}
    </ul>
  </section>
);

Downloads.propTypes = {
  downloads: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string,
  })).isRequired,
};

// Manulife routes by need rather than publishing a single desk, so its two
// destinations get equal weight instead of one leading contact block.
const RoutingPanels = ({ panels }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
    {panels.map((panel) => (
      <Card key={panel.email} size="lg" className="flex flex-col">
        <h2 className="text-base font-semibold text-succession-text-primary">{panel.heading}</h2>
        {panel.body && (
          <p className="mt-2 text-sm leading-relaxed text-succession-text-secondary">{panel.body}</p>
        )}
        <a
          href={`mailto:${panel.email}`}
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-succession-primary underline decoration-succession-primary/30 underline-offset-2 transition-colors hover:decoration-succession-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded">
          <Mail className="h-4 w-4 flex-shrink-0" aria-hidden />
          {panel.email}
        </a>
      </Card>
    ))}
  </div>
);

RoutingPanels.propTypes = {
  panels: PropTypes.arrayOf(PropTypes.shape({
    heading: PropTypes.string,
    body: PropTypes.string,
    email: PropTypes.string,
  })).isRequired,
};

const ContactPage = ({ content }) => {
  if (!content) return null;

  const {
    variant, title, subtitle, intro, contact, sections = [], downloads = [], panels = [],
  } = content;

  return (
    <SingleColumnTemplate title={title} subtitle={subtitle}>
      {intro && (
        <p className="text-base leading-relaxed text-succession-text-secondary">
          {intro}
        </p>
      )}

      {variant === 'panels' ? (
        <RoutingPanels panels={panels} />
      ) : (
        <>
          <ContactCard contact={contact} />
          {sections.map((section) => (
            <ContentSection key={section.heading} heading={section.heading} items={section.items} />
          ))}
          {downloads.length > 0 && <Downloads downloads={downloads} />}
        </>
      )}
    </SingleColumnTemplate>
  );
};

ContactPage.propTypes = {
  content: PropTypes.shape({
    variant: PropTypes.oneOf(['contact', 'panels']),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    intro: PropTypes.string,
    contact: PropTypes.object,
    sections: PropTypes.array,
    downloads: PropTypes.array,
    panels: PropTypes.array,
  }),
};

ContactPage.defaultProps = { content: null };

export default ContactPage;
